import { createApp } from "vue";
import store from "./store";
import App from "./App.vue";
import router from "./router";
import {clickOutside} from "./helpers/clickOutside.js";
import VueToast from "vue-toast-notification";
import "./assets/scss/index.scss";
import "vue-toast-notification/dist/theme-sugar.css";
import VueNativeSock from "vue-native-websocket-vue3";
import mitt from "mitt";
import VueLazyLoad from "vue3-lazyload";
import {useI18n} from "vue-i18n";
import i18n, { resolveLocale, setLocaleCookie, isSupportedLocale } from "./i18n";

const authUrl = import.meta.env.VITE_APP_API_AUTH;
const setI18nLocale = (locale) => {
    if (i18n.global.locale?.value !== undefined) {
        i18n.global.locale.value = locale;
    } else {
        i18n.global.locale = locale;
    }
};

const detectLocaleFromUrl = () => {
    if (typeof window === "undefined") {
        return null;
    }
    const match = /^\/([a-z]{2})(?:\/|$)/i.exec(window.location.pathname);
    const candidate = match?.[1]?.toLowerCase();
    if (candidate && isSupportedLocale(candidate)) {
        return candidate;
    }
    return null;
};

async function bootstrap() {
    const emitter = mitt();
    const app = createApp(App, {
        setup() {
            const {t} = useI18n();
            return {t};
        }
    });
    app.config.globalProperties.emitter = emitter;

    app.config.errorHandler = (err, vm, info) => {
        console.error("[Vue error]", err, info);

        fetch(authUrl + "/identity/js-error-log", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                message: [
                    err.message ? `Message: ${err.message}` : '',
                    err.error?.stack ? `Stack: ${err.error.stack}` : ''
                ].filter(Boolean).join('\n'),
                stack: err.stack,
                info,
                ua: navigator.userAgent,
            }),
        }).catch(() => {});
    };

    const urlLocale = detectLocaleFromUrl();
    const detectedLocale = urlLocale ?? await resolveLocale();
    if (urlLocale) {
        setLocaleCookie(urlLocale);
    }
    const currentLocale = i18n.global.locale?.value ?? i18n.global.locale;
    if (detectedLocale && detectedLocale !== currentLocale) {
        setI18nLocale(detectedLocale);
        await store.dispatch("changeAppLanguage", detectedLocale);
    }

    app.use(store)
        .use(router)
        .use(i18n)
        .use(VueLazyLoad)
        .directive("click-outside", clickOutside)
        .use(VueNativeSock, import.meta.env.VITE_APP_API_SOCKET, {
            store,
            format: "json",
            connectManually: true,
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 5000
        })
        .use(VueToast).mount('#app');
}

bootstrap();
